import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import {
  isBiometricEnabled,
  getBiometricCredentials,
  disableBiometricLogin,
} from '../services/biometricService';
import { getAttribution } from '../utils/analytics';

// Use current domain for API calls (works on both custom domains and preview)
const API_URL = process.env.REACT_APP_BACKEND_URL 
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : `${window.location.origin}/api`;

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // When biometric login is enabled on a native device, defer trusting any
  // stored token until the user passes the Face ID gate on launch.
  const [biometricGate] = useState(() => isBiometricEnabled());
  const [token, setToken] = useState(() =>
    isBiometricEnabled() ? null : localStorage.getItem('token'),
  );

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else if (!biometricGate) {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // On native launch with biometrics enabled: prompt Face ID and re-authenticate
  // with the securely stored credentials. Any failure falls back to manual login.
  useEffect(() => {
    if (!biometricGate) return;
    (async () => {
      try {
        const creds = await getBiometricCredentials();
        if (creds?.email && creds?.password) {
          await login(creds.email, creds.password);
        } else {
          setLoading(false);
        }
      } catch {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API_URL}/auth/me`, { timeout: 15000 });
      setUser(response.data);
      // Acquisition Engine: stitch first/last-touch attribution onto the user
      // once per session (fire-and-forget, never blocks or breaks the app).
      try {
        if (!sessionStorage.getItem('sk_attr_synced')) {
          const attribution = getAttribution();
          if (attribution) {
            axios.post(`${API_URL}/auth/attribution`, { attribution }).catch(() => {});
          }
          sessionStorage.setItem('sk_attr_synced', '1');
        }
      } catch (e) { /* ignore */ }
    } catch (error) {
      console.error('Failed to fetch user:', error);
      localStorage.removeItem('token');
      setToken(null);
      delete axios.defaults.headers.common['Authorization'];
    } finally {
      setLoading(false);
    }
  };

  // Failsafe: never let the app hang on the full-screen auth spinner. If we're
  // still "loading" after 10s (e.g. a wedged native Face ID prompt or a stalled
  // /auth/me on a flaky connection), release the gate so the user always
  // reaches a usable screen (login or app) instead of an infinite spinner.
  useEffect(() => {
    const t = setTimeout(() => setLoading(false), 10000);
    return () => clearTimeout(t);
  }, []);

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/auth/login`, { email, password }, { timeout: 20000 });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const register = async (email, password, name) => {
    const response = await axios.post(`${API_URL}/auth/register`, { email, password, name });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const startTrial = async (email, password, name, weekly_tip_opt_in = true, ref_code = null) => {
    // Attribute the trial to the Live Scores app if the user arrived via a
    // Scores-app "Try Predictions" CTA in the last 30 days.
    let ref_source = null;
    try {
      const ts = parseInt(localStorage.getItem('sk_scores_referral') || '0', 10);
      if (ts && (Date.now() - ts) < 30 * 24 * 60 * 60 * 1000) {
        ref_source = 'scores_app';
      }
    } catch { /* ignore */ }
    const response = await axios.post(`${API_URL}/auth/start-trial`, { email, password, name, weekly_tip_opt_in, ref_code, ref_source });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    try { localStorage.removeItem('sk_scores_referral'); } catch { /* ignore */ }
    return userData;
  };

  /**
   * Set an authenticated session directly from a pre-issued token + user
   * object.
   */
  const setSession = ({ token: newToken, user: userData }) => {
    if (!newToken) return;
    localStorage.setItem('token', newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    if (userData) setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
    // Clear any stored biometric credentials so a manual logout fully signs out.
    disableBiometricLogin();
  };

  const refreshUser = async () => {
    if (token) {
      await fetchUser();
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, startTrial, setSession, logout, refreshUser, token }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
